/**
 * Низкоуровневый REST-клиент для панели оператора.
 *
 * Обёртка над fetch с типизированным ответом, единым ApiError и автоматической
 * ретрай-логикой после refresh access-токена. Сам клиент не знает про
 * authStore — функции `getAccess` / `refresh` / `onAuthFail` передаются
 * извне, что делает модуль чистым и тестируемым.
 */

import { isAccessExpired } from '../lib/jwt';

/** Известные коды ошибок. Сервер шлёт серверные коды; network/auth_required
 * — чисто клиентские. */
export type ApiErrorCode =
  | 'auth_failed'
  | 'tenant_suspended'
  | 'rate_limited'
  | 'invalid_payload'
  | 'not_found'
  | 'forbidden'
  | 'internal'
  | 'network'
  | 'auth_required'
  // ANX-055: signup captcha (Cloudflare Turnstile)
  | 'captcha_required'
  | 'captcha_failed'
  | 'captcha_unavailable'
  // ANX-056a: согласие на обработку ПД при signup'е (152-ФЗ / GDPR)
  | 'consent_required'
  // ANX-105: paywall — enforcement лимитов тарифа (HTTP 402)
  | 'seat_limit_reached'
  | 'dialog_limit_reached'
  | 'plan_required'
  | 'unknown';

/** ANX-105: коды 402, на которые показываем апселл-диалог. */
const PAYWALL_CODES: ReadonlySet<ApiErrorCode> = new Set([
  'seat_limit_reached',
  'dialog_limit_reached',
  'plan_required',
]);

export function isPaywallCode(code: ApiErrorCode): boolean {
  return PAYWALL_CODES.has(code);
}

export class ApiError extends Error {
  override readonly name = 'ApiError';
  readonly code: ApiErrorCode;
  readonly status: number;
  readonly retryAfterMs?: number;

  constructor(code: ApiErrorCode, message: string, status = 0, retryAfterMs?: number) {
    super(message);
    this.code = code;
    this.status = status;
    if (retryAfterMs !== undefined) this.retryAfterMs = retryAfterMs;
  }
}

export interface RequestOptions extends Omit<RequestInit, 'body'> {
  /** Прикреплять Authorization: Bearer. По умолчанию true. */
  auth?: boolean;
  /** JSON-тело — будет сериализовано и проставлен Content-Type. */
  json?: unknown;
}

export interface ApiClient {
  request<T>(path: string, init?: RequestOptions): Promise<T>;
}

export interface ApiClientDeps {
  /** Базовый URL gateway, без trailing slash. В dev — пустая строка
   *  (vite-proxy), в prod — `https://api.anexica.com`. */
  origin: string;
  /** Текущий access-токен из authStore, либо null. */
  getAccess: () => string | null;
  /** Обновить пару токенов и вернуть новый access (или null, если refresh
   *  истёк или невалиден). Внутри: вызов /auth/refresh и authStore.setSession. */
  refresh: () => Promise<string | null>;
  /** Вызывается при невосстановимой потере сессии (refresh fail). */
  onAuthFail: () => void;
  /** ANX-105: вызывается при 402 с paywall-кодом (лимит тарифа) — для
   *  глобального апселл-диалога. Ошибка всё равно пробрасывается. */
  onPaywall?: (code: ApiErrorCode) => void;
}

export function createApiClient(deps: ApiClientDeps): ApiClient {
  let refreshInFlight: Promise<string | null> | null = null;

  async function singleFlightRefresh(): Promise<string | null> {
    if (refreshInFlight) return refreshInFlight;
    refreshInFlight = deps.refresh().finally(() => {
      refreshInFlight = null;
    });
    return refreshInFlight;
  }

  async function send(path: string, opts: RequestOptions, useAuth: boolean): Promise<Response> {
    const headers: Record<string, string> = {};
    if (opts.headers) {
      const existing = opts.headers as Record<string, string>;
      for (const k of Object.keys(existing)) headers[k] = existing[k];
    }
    if (opts.json !== undefined) {
      headers['Content-Type'] = 'application/json';
    }
    if (useAuth) {
      const token = deps.getAccess();
      if (!token) {
        throw new ApiError('auth_required', 'no access token in store');
      }
      headers.Authorization = `Bearer ${token}`;
    }
    const init: RequestInit = { ...opts, headers };
    if (opts.json !== undefined) {
      init.body = JSON.stringify(opts.json);
    }
    delete (init as { json?: unknown }).json;
    delete (init as { auth?: boolean }).auth;

    try {
      return await fetch(`${deps.origin}${path}`, init);
    } catch (e) {
      throw new ApiError('network', e instanceof Error ? e.message : 'network error');
    }
  }

  async function parseError(resp: Response): Promise<ApiError> {
    let code: ApiErrorCode = 'unknown';
    let message = `HTTP ${resp.status}`;
    let retryAfterMs: number | undefined;
    try {
      const body = (await resp.json()) as {
        code?: string;
        message?: string;
        retry_after_ms?: number;
      };
      if (body && typeof body.code === 'string') code = body.code as ApiErrorCode;
      if (body && typeof body.message === 'string') message = body.message;
      if (body && typeof body.retry_after_ms === 'number') retryAfterMs = body.retry_after_ms;
    } catch {
      // not JSON — defaults
    }
    return new ApiError(code, message, resp.status, retryAfterMs);
  }

  async function readBody<T>(resp: Response): Promise<T> {
    if (resp.status === 204) return undefined as T;
    const text = await resp.text();
    if (text === '') return undefined as T;
    return JSON.parse(text) as T;
  }

  return {
    async request<T>(path: string, opts: RequestOptions = {}): Promise<T> {
      const useAuth = opts.auth !== false;

      // ANX-117: если access-токен уже истёк — обновляем ДО отправки, чтобы не
      // слать протухший запрос (в проде он давал «failed to fetch», а не 401,
      // из-за чего оператора не уводило на логин). Refresh провалился →
      // детерминированный onAuthFail (редирект на логин), а не сетевая ошибка.
      if (useAuth) {
        const token = deps.getAccess();
        if (token && isAccessExpired(token, Date.now())) {
          const refreshed = await singleFlightRefresh();
          if (!refreshed) {
            deps.onAuthFail();
            throw new ApiError('auth_required', 'session expired');
          }
        }
      }

      let resp = await send(path, opts, useAuth);

      if (resp.status === 401 && useAuth) {
        const newAccess = await singleFlightRefresh();
        if (!newAccess) {
          deps.onAuthFail();
          throw await parseError(resp);
        }
        resp = await send(path, opts, true);
      }

      if (!resp.ok) {
        const err = await parseError(resp);
        if (err.status === 402 && isPaywallCode(err.code)) {
          deps.onPaywall?.(err.code);
        }
        throw err;
      }
      return readBody<T>(resp);
    },
  };
}
